<template>
  <div>
    <div class="row">
      <div class="col-12 col-md-5 mb-3">
        <label class="wfh-label">Status</label>
        <div class="wfh-v-select-override wfh-sm">
          <v-select
              :options="statusOptions"
              :filterable="false"
              :clearable="false"
              v-model="statusSelection"
              :reduce="(item) => item.value"
          >
          </v-select>
        </div>
      </div>

      <div class="col-12 col-md-7 mb-3">
        <label class="wfh-label">Date Range</label>
        <div class="wfh-v-ctk-datepicker-override wfh-sm">
          <v-ctk-datepicker
              format="YYYY-MM-DD"
              formatted="YYYY-MM-DD"
              v-model="dateRange"
              :no-label="true"
              :only-date="true"
              :range="true"
          />
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col mb-3">
        <label class="wfh-label">Team</label>
        <div class="wfh-v-select-override wfh-sm">
          <v-select
              :options="teamOptions"
              :filterable="false"
              :clearable="false"
              v-model="selectedTeam"
              :reduce="(item) => item.value"
          >
          </v-select>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-auto ml-auto">
        <button class="btn wfh-btn-link-primary p-0" @click="openAdvancedFilterModal()">
          Advanced Filter
        </button>
      </div>
    </div>

    <div class="row mb-2" v-if="selectedTeamMember && teamMembersOptions.length > 0">
      <div class="col">
        <label class="wfh-label">Team Members</label>
        <div class="wfh-v-select-override wfh-sm">
          <v-select
              :options="teamMembersOptions"
              :clearable="true"
              :value="selectedTeamMember"
              @input="handleSelectTeamMember"
              :reduce="(item) => item.value"
              @search="searching"
          >
          </v-select>
        </div>
      </div>
    </div>

    <div class="row mb-2" v-if="selectedLabel">
      <div class="col">
        <label class="wfh-label">Label</label>
        <div class="wfh-v-select-override">
          <v-select
              :options="companyLabels"
              :filterable="true"
              :clearable="true"
              v-model="selectedLabel"
              @input="handleSelectLabel"
              :reduce="(item) => item.value"
              placeholder="Select a label"
          >
          </v-select>
        </div>
      </div>
    </div>

    <div class="row mb-2" v-for="field of customFilterFields">
      <div class="col-12">
        <label class="wfh-label">{{ field.label }}</label>
        <div class="wfh-v-select-override">
          <v-select
              :options="field.options"
              :filterable="true"
              :clearable="true"
              v-model="field.selected"
              :reduce="handleSelection"
              @input="handleFilter"
              placeholder="Select a value"
          >
          </v-select>
        </div>
      </div>
    </div>

    <div class="py-2">
      <div v-if="ready">
        <task-item
            v-for="(task, key) in tasks"
            :task="task"
            :key="`todo-${key}-${task.id}`"
            :isPersonal="isPersonalTask(task)"
            :viewOnly="(!isPersonalTask(task) || !isCreatedTask(task)) && !task.isCollaboration"
            :isCollaborator="task.isCollaboration"
            :labelOptions="labelOptions"
            @taskUpdated="taskUpdated"
            @taskDeleted="taskDeleted"
            @saveLabel="saveLabel"
            @deleteLabel="deleteLabel"
        ></task-item>

        <task-item
            v-for="task in goals"
            :task="task"
            :key="`goal-${task.id}`"
            :viewOnly="!isPersonalTask(task) || !isCreatedTask(task)"
            :labelOptions="labelOptions"
            @taskUpdated="taskUpdated"
            @taskDeleted="taskDeleted"
            @saveLabel="saveLabel"
            @deleteLabel="deleteLabel"
        ></task-item>
      </div>

      <div v-else class="text-center w-100 py-3">
        <div class="spinner-border" role="status">
          <span class="sr-only">Loading...</span>
        </div>
      </div>

      <div class="row">
        <div class="col-auto mx-auto">
          <button
              class="btn wfh-btn-link-primary"
              :disabled="page >= lastPage || this.tasks.length === 0"
              @click="fetchNextTasks()"
          >
            Show More
          </button>
        </div>
      </div>
    </div>

    <transition name="fade">
      <advanced-task-filter-modal
          :existingFilters="advancedFilters"
          :cachedFields="customFilterFields"
          v-if="showAdvancedFilterModal"
          :selectedTeam="selectedTeam"
          :searchQueryCached="memberSearchQuery"
          @updateSearchQuery="searchQuery => this.memberSearchQuery = searchQuery"
          @close="closeAdvancedTaskFilterModal"
          @submit="handleAdvancedFilter"
      ></advanced-task-filter-modal>
    </transition>
  </div>
</template>

<script>
import Task from '../Task';
import vSelect from 'vue-select';
import { STATUS_OPTIONS, DATE_RANGE_OPTIONS } from './DefaultOptions';
import buildAdvanceFilters from '../../../utils/buildAdvanceFilters';
import {
  getCustomAttributes,
  getSelectedTeamMember,
  getSelectedLabel,
  getAdvancedFilters,
  setCustomAttributes,
  setSelectedLabel,
  setSelectedTeamMember,
  setAdvancedFilters,
} from '../../../repositories/advancedFiltersPersistence';
import {
  getTeamMemberSearchQuery,
  getSelectedTeam,
  getDateRange,
  getStatusSelection,
  setTeamMemberSearchQuery,
  setStatusSelection,
  setSelectedTeam,
  setDateRange,
} from '../../../repositories/teamTasksPagePersistence';

const CUSTOM_ATTRIBUTE_RELATIONSHIP = 'customAttributes';

export default {
  components: {
    vSelect,
    buildAdvanceFilters,
    getAdvancedFilters,
    getSelectedTeamMember,
    getTeamMemberSearchQuery,
    getSelectedLabel,
    getSelectedTeam,
    getDateRange,
    getStatusSelection,
    setCustomAttributes,
    setSelectedLabel,
    setSelectedTeamMember,
    setAdvancedFilters,
    setTeamMemberSearchQuery,
    setStatusSelection,
    setSelectedTeam,
    setDateRange,
  },

  props: ['teamTasks', 'teamGoals'],

  data () {
    return {
      title: '',
      tasks: this.teamTasks?.data ?? [],
      taskService: Task,
      page: this.teamTasks?.pagination.page ?? 1,
      lastPage: this.teamTasks?.pagination.lastPage ?? 1,
      utcOffset: true,
      goals: this.teamGoals ?? [],
      assignedToSelection: null,
      statusSelection: getStatusSelection() ?? '',
      dateRange: getDateRange() ?? null,
      ready: true,
      selectedTeam: this.getPersistedSelectedTeam(),
      labelOptions: [],
      statusOptions: STATUS_OPTIONS,
      dateRangeOptions: DATE_RANGE_OPTIONS,
      showAdvancedFilterModal: false,
      customFilterFields: this.getPersistedCustomFilterFields(),
      advancedFilters: this.getPersistedAdvancedFilter(),
      selectedLabel: this.getPersistedLabel(),
      selectedTeamMember: this.getPersistedTeamMember(),
      memberSearchQuery: this.getPersistedTeamMemberSearchQuery(),
      teamMembersOptions: [],
    };
  },

  watch: {
    statusSelection () {
      setStatusSelection(this.statusSelection);
      this.reset();
      this.fetchTasks();
    },

    dateRange () {
      setDateRange(this.dateRange);

      if ((this.dateRange !== null && this.dateRange.end !== null && this.dateRange.start !== null) || this.dateRange === null) {
        this.reset();
        this.fetchTasks();
      }
    },

    selectedTeam () {
      setSelectedTeam(this.selectedTeam);
      this.reset();
      this.resetSelectedTeamMember();
      this.fetchTasks();
    },

    advancedFilters () {
      setAdvancedFilters(this.advancedFilters);
    },

    memberSearchQuery () {
      if (this.memberSearchQuery === null) {
        setTeamMemberSearchQuery();
        return;
      }

      setTeamMemberSearchQuery(this.memberSearchQuery);
    },
  },

  computed: {
    teams () {
      return this.$store.state.userTeams;
    },

    currentUser () {
      return this.$store.state.currentUser;
    },

    timezone () {
      return this.$store.state.timezone;
    },

    teamOptions () {
      let options = [
        {
          label: 'All',
          value: '',
        },
      ];

      for (const team of this.teams) {
        options.push({
          label: `${team.name} Team`,
          value: team.id,
        });
      }

      return options;
    },

    companyLabels () {
      const options = this.$store.state.labelOptions.map((label) => {
        return {
          value: label.id,
          label: label.name,
        };
      });

      if (!options) {
        return [];
      }

      return options;
    },
  },

  methods: {
    searching: _.debounce(async function (searchQuery) {
      if (!!searchQuery) {
        this.memberSearchQuery = searchQuery;
        await this.fetchTeamMembers(searchQuery);
      }
    }, 500),

    getPersistedCustomFilterFields () {
      return getCustomAttributes() ?? [];
    },

    getPersistedAdvancedFilter () {
      return getAdvancedFilters() ?? [];
    },

    getPersistedTeamMemberSearchQuery () {
      return getTeamMemberSearchQuery();
    },

    getPersistedSelectedTeam () {
      const team = getSelectedTeam();

      return team ? parseInt(team) : '';
    },

    getPersistedLabel () {
      return getSelectedLabel();
    },

    getPersistedTeamMember () {
      return getSelectedTeamMember();
    },

    fetchNextTasks () {
      this.page++;
      this.fetchTasks();
    },

    taskUpdated (updatedTask) {
      this.tasks = this.tasks.map(task => {
        if (task.id === updatedTask.id) {
          Object.keys(updatedTask).forEach(key => {
            task[key] = updatedTask[key];
          });
        }

        return task;
      });
    },

    taskDeleted(deletedTask) {
      if (deletedTask.type === "todo") {
        this.deleteFromTodoTaskList(deletedTask);
      } else if (deletedTask.type === "hourly") {
        this.deleteFromHourlyTaskList(deletedTask);
      }
    },

    deleteFromTodoTaskList(deletedTask) {
      this.tasks = this.tasks.filter(task => {
        return task.id !== deletedTask.id;
      });
    },

    deleteFromHourlyTaskList(deletedTask) {
      this.hourlyTasks = this.hourlyTasks.map(task => {
        if (task.id === deletedTask.id) {
          task = {
            title: "",
            type: "hourly",
            completed_at: task.completed_at
          };
        }

        return task;
      });
    },

    async saveLabel (data) {
      const { key, val } = data;

      if (key) {
        this.labelOptions = this.labelOptions.map((value, labelKey) => {
          if (labelKey === key) {
            return {
              id: labelKey,
              name: val,
            };
          }

          return value;
        });
      } else {
        this.labelOptions.push({
          id: this.labelOptions.length,
          name: val,
        });
      }
    },

    deleteLabel (labelKey) {
      const newLabels = this.labelOptions.filter((value, key) => {
        return key !== labelKey;
      });

      this.labelOptions = newLabels.map((value, key) => {
        return {
          id: key,
          name: value.name,
        };
      });
    },

    fetchTasks: _.debounce(async function () {
      try {
        const filters = {
          assigned_to: 'team',
        };
        const filterKeys = ['dateRange', 'page', 'statusSelection', 'selectedTeam'];
        const filterKeyMap = {
          dateRange: 'range',
          page: 'page',
          statusSelection: 'status',
          selectedTeam: 'team_id',
        };

        for (const filterKey of filterKeys) {
          if (this[filterKey]) {
            let value = this[filterKey];

            if (filterKey === 'dateRange' && typeof this.dateRange === 'object' && this.dateRange !== null) {
              value = [this.dateRange.start, this.dateRange.end];
            }

            filters[filterKeyMap[filterKey]] = value;
          }
        }

        if (this.advancedFilters !== null) {
          filters['advanced_filters'] = this.advancedFilters;
        }

        const data = await this.taskService.fetchTasks(filters);
        let tasks = data.data;

        let tasksWithSubtasks = [];

        tasks.forEach(item => {
          item.isCollaboration = this.isCollaborator(item);
          if (item.total_subtasks > 0) {
            tasksWithSubtasks.push(item.id);
          }
        });

        if (tasksWithSubtasks.length > 0) {
          const res = await axios.get(`/task/subtasks?ids=${tasksWithSubtasks.join(',')}`);
          const subtasks = res.data.data;

          tasks = tasks.map(item => {
            const subtask = subtasks[item.id];

            if (!!subtask) {
              item['subtasks'] = subtask;
            }

            return item;
          });
        }

        this.tasks = [
          ...this.tasks,
          ...tasks,
        ];

        this.page = data.current_page;
        this.lastPage = data.last_page;

        this.$emit('storeTasksLocally', {
          key: 'teamTasks',
          tasks: {
            data: this.tasks,
            pagination: {
              page: this.page,
              lastPage: this.lastPage,
            },
          },
        });
      } finally {
        this.ready = true;
      }
    }, 500),

    async fetchGoals () {
      const data = await this.taskService.fetchTeamGoals();
      this.goals = data.map(item => {
        item.title = item.name;
        item.labels = [item.label];

        return item;
      });

      this.$emit('storeTasksLocally', {
        key: 'teamGoals',
        tasks: this.goals,
      });
    },

    reset () {
      this.tasks = [];
      this.page = 1;
      this.ready = false;
    },

    resetSelectedTeamMember () {
      this.advancedFilters = this.advancedFilters.filter((filter) => filter.type !== 'selectedTeamMember');
      this.memberSearchQuery = null;
      this.selectedTeamMember = null;
      setTeamMemberSearchQuery();
      setSelectedTeamMember();
    },

    prepareLabels () {
      this.labelOptions = this.currentUser.labels.map((value, key) => {
        return {
          id: key,
          name: value,
        };
      });
    },

    isPersonalTask (task) {
      return (task.users.filter(user => user.id === this.currentUser.id)).length > 0;
    },

    isCreatedTask (task) {
      return task.created_by === this.currentUser.id;
    },

    isCollaborator (task) {
      let taskTeamIds = [];
      const taskUsers = task.users;
      const collaboratorAccessTeamIds = this.teams.filter((team) => team.pivot.role === 'collaborator').
          map((team) => team.id);

      for (const user of taskUsers) {
        if (user.hasOwnProperty('groups')) {
          for (const group of user.groups) {
            if (!taskTeamIds.includes(group.id)) {
              taskTeamIds.push(group.id);
            }
          }
        }
      }

      return taskTeamIds.filter((groupId) => collaboratorAccessTeamIds.includes(groupId)).length > 0;
    },

    openAdvancedFilterModal () {
      this.showAdvancedFilterModal = true;
    },

    closeAdvancedTaskFilterModal () {
      this.showAdvancedFilterModal = false;
    },

    async handleAdvancedFilter (filters) {
      this.advancedFilters = filters;
      this.customFilterFields = this.getPersistedCustomFilterFields();
      this.selectedLabel = this.getPersistedLabel();
      this.selectedTeamMember = this.getPersistedTeamMember();

      this.reset();
      this.closeAdvancedTaskFilterModal();

      await this.fetchTeamMembers(getTeamMemberSearchQuery());
      await this.fetchTasks();
    },

    async handleFilter () {
      this.advancedFilters = buildAdvanceFilters(
          this.selectedTeam,
          this.selectedLabel,
          this.selectedTeamMember,
          this.customFilterFields,
      );

      setCustomAttributes(this.customFilterFields);
      setSelectedLabel(this.selectedLabel);
      setSelectedTeamMember(this.selectedTeamMember);

      this.reset();
      this.closeAdvancedTaskFilterModal();

      await this.fetchTeamMembers(getTeamMemberSearchQuery());
      await this.fetchTasks();
    },

    handleSelection (item) {
      return item.value ?? item;
    },

    async handleSelectLabel (value) {
      this.selectedLabel = value;
      this.customFilterFields = [];

      await this.fetchLabelCustomAttributes();
      await this.handleFilter();
    },

    async fetchLabelCustomAttributes () {
      const res = await axios.get(`/labels/${this.selectedLabel}/custom-attributes?for_filter=1`);

      this.customFilterFields = res.data.data.map((attribute) => {
        let value = null;

        for (const existingFilter of this.advancedFilters) {
          if (existingFilter.type === CUSTOM_ATTRIBUTE_RELATIONSHIP && existingFilter.id === attribute.id) {
            value = existingFilter.value;
          }
        }

        return {
          id: attribute.id,
          label: attribute.label,
          selected: value,
          options: _.uniq(attribute.options.filter((option) => {
            return option !== null;
          })),
        };
      });
    },

    async handleSelectTeamMember (value) {
      this.selectedTeamMember = value;

      if (value === null) {
        setTeamMemberSearchQuery();
      }

      await this.handleFilter();
    },

    async fetchTeamMembers (searchQuery = null) {
      this.memberSearchQuery = searchQuery;

      const params = { per_page: 5 };

      if (searchQuery) {
        params.q = searchQuery;
      }

      if (!!this.selectedTeam) {
        const res = await axios.get(`/groups/${this.selectedTeam}/members`, { params });

        this.teamMembersOptions = res.data.data.map((teamMember) => {
          return {
            value: teamMember.id,
            label: teamMember.display_name ?? teamMember.name,
          };
        });

        return;
      }

      const res = await axios.get(`/team-members`, { params });

      this.teamMembersOptions = res.data.data.map((teamMember) => {
        return {
          value: teamMember.id,
          label: teamMember.display_name ?? teamMember.name,
        };
      });
    },

    registerTaskUpdatedListener () {
      window.Echo.private(`tasks-updated.${this.currentUser.company.id}`).listen(
          '.tasks-updated',
          e => {
            this.reset();
            this.fetchTasks();
          },
      );
    },
  },

  mounted () {
    this.registerTaskUpdatedListener();

    if (!this.teamTasks) {
      this.ready = false;
      this.fetchTasks();
    }

    if (!this.teamGoals) {
      this.fetchGoals();
    }

    if (this.selectedTeamMember) {
      this.fetchTeamMembers(getTeamMemberSearchQuery());
    }

    this.prepareLabels();
  },
};
</script>
